// @ts-nocheck

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LanguageIcon from '@mui/icons-material/Language';
import CodeIcon from '@mui/icons-material/Code';
import AssessmentIcon from '@mui/icons-material/Assessment';
import BuildIcon from '@mui/icons-material/Build';
import { Backdrop, Box, Chip, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';

import CustomFooter from '../../components/Footer/CustomFooter';
import PageHeader from '../../components/PageHeader';
import CustomPaper from '../DFMPage/components/CustomPaper/CustomPaper';
import EditButton from '../DFMPage/components/EditButton/EditButton';
import ModernSection from '../DFMPage/components/ModernSection/ModernSection';

import { paths } from '../../routes/paths';

import {
  useCreateUpdateCompanyTechUseMutation,
  useDeleteCompanyTechUseMutation,
  useGetStackshareToolCategoriesQuery,
  useGetStackshareToolsLazyQuery,
  useGetTechnologyUseQuery,
  useAddTechnologyFindingUrlMutation,
  useAddTechnologyFindingTextMutation,
  useGetCompanyProductsLazyQuery,
  useGetProgrammingLanguagesLazyQuery,
  usePrecalculatedKpisLazyQuery,
} from '../../gql/generated/graphql';
import { getNodes } from '../../gql/helpers';
import { handleVisitPage } from '../../helpers/helpers';
import useResponsive from '../../hooks/useResponsive';
import { TechnologyCategories, ValidationSteps } from '../ValidationPage/types';
import TechnologySelect from '../ValidationPage/components/TechnologySelect/TechnologySelect';
import { validateTechnologyStyles } from '../ValidationPage/components/ValidationSteps/styles';
import { precalculatedTechKpisColumns } from '../DFMPage/helpers';
import ProductSection from '../DFMPage/components/ProductSection/ProductSection';
import { TrackASteps } from '../ValidationPageV2/types';
import styles from './styles';

export type TechnologyType = {
  category: string | null;
  technology: string | null;
  stackshareToolId: number | null;
};

const TechAssessmentPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { sm } = useResponsive();
  const navigate = useNavigate();

  const [technReferenceUrl, setTechnReferenceUrl] = useState('');
  const [technFindingText, setTechnFindingText] = useState('');
  const [selectedTechIds, setSelectedTechIds] = useState<number[]>([]);
  const [technology, setTechnology] = useState<TechnologyType>({
    category: null,
    technology: null,
    stackshareToolId: null,
  });

  const companyParamsId = searchParams.get('companyId');
  const companyId = companyParamsId ? parseInt(companyParamsId) : undefined;

  const [getCompanyProducts, { data: companyProducts, loading: companyProductsLoading }] =
    useGetCompanyProductsLazyQuery();

  const [getProgrammingLanguages, { data: programmingLanguages, loading: getProgrammingLanguagesLoading }] =
    useGetProgrammingLanguagesLazyQuery();

  const [getPrecalculatedKpis, { data: precalculatedKpis, loading: precalculatedKpisLoading }] =
    usePrecalculatedKpisLazyQuery();

  const {
    data: technologyUsed,
    loading: technologyUsedLoading,
    refetch: technologyUsedRefetch,
  } = useGetTechnologyUseQuery({
    variables: { companyId },
    skip: !companyId,
  });

  const { data: stackshareToolCategories, loading: stackshareToolCategoriesLoading } =
    useGetStackshareToolCategoriesQuery();

  const [getStackshareTools, { data: stackshareTools, loading: stackshareToolsLoading }] =
    useGetStackshareToolsLazyQuery();

  const [deleteTechnology, { loading: deleteTechnologyLoading }] = useDeleteCompanyTechUseMutation();

  const [createUpdateCompanyTechUse, { loading: createUpdateCompanyTechUseLoading }] =
    useCreateUpdateCompanyTechUseMutation();

  const [addTechnologyFindingUrl, { loading: addTechnologyFindingUrlLoading }] = useAddTechnologyFindingUrlMutation();

  const [addTechnologyFindingText, { loading: addTechnologyFindingTextLoading }] =
    useAddTechnologyFindingTextMutation();

  useEffect(() => {
    if (companyId) {
      getCompanyProducts({ variables: { companyId } });
      getProgrammingLanguages({ variables: { companyId } });
      getPrecalculatedKpis({ variables: { companyId } });
    }
  }, [companyId]);

  const companyProductsNodes = getNodes(companyProducts?.getCompanyProducts);
  const programmingLanguagesNodes = getNodes(programmingLanguages?.getProgrammingLanguages);
  const precalculatedKpisNodes = precalculatedKpis?.companyAssesment?.edges?.[0]?.node?.precalculatedKpis?.edges?.map(
    node => node?.node
  );

  const mobilePaperStyle = sm && styles.mobilePaper;

  const stackshareToolCategoriesNodes = getNodes(stackshareToolCategories?.getStackshareToolCategories);
  const stackshareToolsNodes = getNodes(stackshareTools?.getStackshareTools);

  const technologyUsedNodes = technologyUsed?.getCompany?.edges?.[0]?.node?.techUse?.edges?.map(techUseEdge => {
    const techUseNode = techUseEdge?.node;
    const companyNode = technologyUsed?.getCompany?.edges?.[0]?.node;

    return {
      id: techUseNode?.stackshareTool?.id,
      type: techUseNode?.stackshareTool?.breadcrumb,
      name: techUseNode?.stackshareTool?.name,
      stacksUsed: techUseNode?.stackshareTool?.stacksUsed,
      votes: techUseNode?.stackshareTool?.votes,
      crawlUrl: companyNode?.crawlUrl,
      findingUrl: techUseNode?.findingUrl,
      dbId: techUseNode?.dbId,
    };
  });

  const technologyColumns = [
    {
      field: 'type',
      headerName: t('validation.validateTechnology.categoryColumnLabel'),
      width: 200,
    },
    {
      field: 'name',
      headerName: t('validation.validateTechnology.toolColumnLabel'),
      width: 200,
    },
    {
      field: 'stacksUsed',
      headerName: t('validation.validateTechnology.stacksUsedColumnLabel'),
      width: 200,
    },
    {
      field: 'votes',
      headerName: t('validation.validateTechnology.votesColumnLabel'),
      width: 100,
    },
    {
      field: 'actions',
      headerName: '',
      width: 200,
      editable: false,
      sortable: false,
      renderCell: params => (
        <>
          <IconButton size="small" onClick={() => handleOpenPage(params?.row?.crawlUrl, params?.row?.findingUrl)}>
            <Tooltip title={t('validation.validateTechnology.rowIconTooltipVisit')}>
              <LanguageIcon fontSize="small" />
            </Tooltip>
          </IconButton>
          <IconButton size="small" onClick={() => handleDelete(params.row)}>
            <Tooltip title={t('validation.validateTechnology.rowIconTooltipDelete')}>
              <DeleteOutlineIcon fontSize="small" />
            </Tooltip>
          </IconButton>
        </>
      ),
    },
  ];

  const handleOpenPage = (crawlUrl: string, findingUrl: string) => {
    const fullUrl = `${crawlUrl}${findingUrl}`;
    handleVisitPage(fullUrl);
  };

  const handleDelete = async row => {
    const selectedTechnology = technologyUsed?.getCompany?.edges?.[0]?.node?.techUse?.edges?.find(
      node => node?.node?.stackshareTool?.id === row?.id
    );

    if (!selectedTechnology?.node?.dbId) return;

    const result = await deleteTechnology({
      variables: { techUseId: selectedTechnology?.node?.dbId },
    });

    if (result?.data?.deleteCompanyTechUse?.success) {
      await technologyUsedRefetch();
    }
  };

  const handleDeleteSelectedTechnologies = async (techIds: number[]) => {
    try {
      const promises = techIds?.map(techId =>
        deleteTechnology({
          variables: { techUseId: techId },
        })
      );

      await Promise.all(promises);
      await technologyUsedRefetch();
    } catch (e) {
      console.error('Error during deletion of technologies:', e);
    }
  };

  const handleTechnologyChange = async (name: string, value: string | null) => {
    if (!value) return;

    const selectedTechnology = stackshareToolsNodes.find(item => item?.name === value);

    setTechnology((prevState: TechnologyType) => ({
      ...prevState,
      [name]: value,
      stackshareToolId: selectedTechnology?.dbId,
    }));

    if (name === TechnologyCategories.CATEGORY) {
      await getStackshareTools({ variables: { category: value } });
      setTechnology(prevState => ({
        ...prevState,
        technology: null,
      }));
    }
  };

  const handleChangeTechReferenceUrl = (url: string) => {
    setTechnReferenceUrl(url);
  };

  const handleChangeTechFindingText = (text: string) => {
    setTechnFindingText(text);
  };

  const handleAddTechnology = async () => {
    if (!companyId) return;

    const result = await createUpdateCompanyTechUse({
      variables: {
        companyId,
        stackshareToolId: technology.stackshareToolId,
      },
    });

    if (result?.data?.createUpdateCompanyTechUse?.companyId) {
      setTechnology({
        category: null,
        technology: null,
        stackshareToolId: null,
      });
      await technologyUsedRefetch();
    }
  };

  const handleAddTechnologyFindingUrl = async () => {
    try {
      if (!technReferenceUrl || !companyId) return;

      const result = await addTechnologyFindingUrl({
        variables: {
          companyId,
          findingUrl: technReferenceUrl,
        },
      });

      if (result?.data?.addTechnologyFindingUrl?.findingUrl) {
        setTimeout(() => {
          technologyUsedRefetch();
        }, 30000);
      }
    } catch (error) {
      console.error('Error adding technology finding URL:', error);
    }
  };

  const handleAddTechnologyFindingText = async () => {
    try {
      if (!technFindingText || !companyId) return;

      const result = await addTechnologyFindingText({
        variables: {
          companyId,
          findingText: technFindingText,
        },
      });

      if (result?.data?.addTechnologyFindingText?.findingText) {
        setTimeout(() => {
          technologyUsedRefetch();
        }, 30000);
      }
    } catch (error) {
      console.error('Error adding technology finding text:', error);
    }
  };

  const handleEdit = (step: ValidationSteps) => {
    navigate(`${paths.validation}?companyId=${companyParamsId}&step=${step}`);
  };

  const handleEditProducts = () => {
    navigate(
      `${paths.validationV2}?companyId=${companyParamsId}&track=company&step=${TrackASteps.PRODUCTS_BUSINESS_MODEL}`
    );
  };

  const companyName = companyId ? `Company ${companyId}` : null;

  return (
    <Box>
      <PageHeader
        title="Tech Assessment"
        subtitle="Analyze technology stack and usage statistics"
        variant="default"
        breadcrumbs={
          companyId
            ? [{ label: 'Home', path: '/' }, { label: 'Evaluation', path: '/evaluation' }, { label: 'Tech Assessment' }]
            : undefined
        }
      />
      {!companyId ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="200px">
          <Typography variant="h6" color="text.secondary">
            Please select a company to view technology assessment
          </Typography>
        </Box>
      ) : (
        <Box mt={1} gap={0} display="flex" flexDirection="column">
          {/* Products Section */}
          <ProductSection
            products={companyProductsNodes || []}
            loading={companyProductsLoading}
            headerActions={
              <Tooltip title="Edit products" arrow>
                <IconButton size="small" onClick={handleEditProducts}>
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            }
          />

          {/* Key Technologies Section */}
          <ModernSection
            title={t('assessment.keyTechnologyTitle')}
            subtitle="Programming languages and core technologies"
            icon={CodeIcon}
            collapsible
            defaultExpanded={true}
            count={programmingLanguagesNodes?.length || 0}
            loading={getProgrammingLanguagesLoading}
            headerActions={<EditButton onPress={handleEdit} step={ValidationSteps.VALIDATE_TECHNOLOGY} />}
          >
            <Box display="flex" flexWrap="wrap" gap={1}>
              {programmingLanguagesNodes?.map(tag => (
                <Chip
                  key={tag?.dbId}
                  label={tag?.name}
                  color="primary"
                  variant="outlined"
                  sx={{
                    fontWeight: 'medium',
                    '&:hover': {
                      backgroundColor: 'primary.light',
                      color: 'primary.contrastText',
                    },
                  }}
                />
              ))}
            </Box>
          </ModernSection>

          {/* Technology KPIs Section */}
          <ModernSection
            title={t('assessment.technologyKpisTitle')}
            subtitle="Key performance indicators for technology"
            icon={AssessmentIcon}
            collapsible
            defaultExpanded={false}
            count={precalculatedKpisNodes?.length || 0}
            loading={precalculatedKpisLoading}
          >
            <DataGrid
              rows={precalculatedKpisNodes || []}
              columns={precalculatedTechKpisColumns}
              loading={precalculatedKpisLoading}
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: 5,
                  },
                },
              }}
              pageSizeOptions={[5]}
              disableRowSelectionOnClick
              sx={{
                '& .MuiDataGrid-root': {
                  border: 'none',
                },
                '& .MuiDataGrid-cell': {
                  borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
                },
              }}
            />
          </ModernSection>

          {/* Technology Stack Management Section */}
          <ModernSection
            title="Technology Stack Management"
            subtitle="Manage and track technology stack components"
            icon={BuildIcon}
            collapsible
            defaultExpanded={true}
            count={technologyUsedNodes?.length || 0}
            loading={technologyUsedLoading || stackshareToolCategoriesLoading}
          >
            <Box display="flex" flex={1} flexDirection="column" gap={2}>
              <DataGrid
                sx={{
                  ...validateTechnologyStyles.dataGrid,
                  '& .MuiDataGrid-root': {
                    border: 'none',
                  },
                  '& .MuiDataGrid-cell': {
                    borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
                  },
                }}
                disableRowSelectionOnClick
                rows={technologyUsedNodes || []}
                columns={technologyColumns}
                loading={technologyUsedLoading}
                rowSelectionModel={selectedTechIds}
                getRowId={row => row?.dbId}
                onRowSelectionModelChange={(ids: number[]) => setSelectedTechIds(ids)}
                rowHeight={35}
                initialState={{
                  pagination: {
                    paginationModel: {
                      pageSize: 10,
                    },
                  },
                }}
                pageSizeOptions={[10]}
                checkboxSelection
                slots={{
                  footer: () => (
                    <CustomFooter
                      onRefresh={() => technologyUsedRefetch()}
                      onDeleteSelected={() => handleDeleteSelectedTechnologies(selectedTechIds)}
                    />
                  ),
                }}
              />

              <TechnologySelect
                categories={stackshareToolCategoriesNodes || []}
                technologies={stackshareToolsNodes || []}
                technReferenceUrl={technReferenceUrl}
                technFindingText={technFindingText}
                state={technology}
                onChangeTechnology={handleTechnologyChange}
                onChangeTechReferenceUrl={handleChangeTechReferenceUrl}
                onChangeTechFindingText={handleChangeTechFindingText}
                onHandleAddTechnology={handleAddTechnology}
                onHandleAddTechnologyFindingUrl={handleAddTechnologyFindingUrl}
                onHandleAddTechnologyFindingText={handleAddTechnologyFindingText}
                technologiesLoading={stackshareToolsLoading}
                categoriesLoading={stackshareToolCategoriesLoading}
              />
            </Box>
          </ModernSection>

          <Backdrop
            open={
              deleteTechnologyLoading ||
              createUpdateCompanyTechUseLoading ||
              addTechnologyFindingUrlLoading ||
              addTechnologyFindingTextLoading
            }
          />
        </Box>
      )}
    </Box>
  );
};

export default TechAssessmentPage;
